import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Card, CircularProgress, Container, Stack, TextField, Typography, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { DatePicker } from '@mui/lab';
import moment from 'moment';
import { dispatch, useSelector } from '../../../redux/store';
import { PATH_DASHBOARD } from '../../../routes/paths';
import useSettings from '../../../hooks/useSettings';
import Page from '../../../components/Page';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
import DataGridTable from '../../../components/table/DataGridTable';
import { accessVerify, safeJSONParse, utcMoment } from '../../../utils/common';
import { toast } from 'react-toastify';
import PermissionRequired from '../../errorPages/permissionRequired';
import { getCustomers } from '../../../redux/slices/customerRedux/customerRedux';
import { fCurrency } from '../../../utils/formatNumber';
import SmsCounter from '../../../utils/smsCounter';

const BATCH_SIZE = 10000000;
const COST_PER_MESSAGE = 0.5;

const replaceSmsPlaceholder = (content, placeholder, value) => {
  return content.split(placeholder).join(String(value));
};

const applySmsPlaceholders = (content, customer) => {
  return replaceSmsPlaceholder(replaceSmsPlaceholder(content, '--name--', customer?.customer_name || ''), '--loyaltypoints--', customer?.total_amount?.toFixed(2) ?? 0);
};

const getValidMonthDay = (date) => {
  if (!date) return null;

  const parsedDate = moment(date);
  if (!parsedDate.isValid()) return null;

  return {
    month: Number(parsedDate.format('MM')),
    day: Number(parsedDate.format('DD')),
  };
};

const formatBirthday = (date) => {
  const birthday = getValidMonthDay(date);
  if (!birthday) return '-';

  return moment({ month: birthday.month - 1, date: birthday.day }).format('DD MMMM');
};

export default function BulkSms() {
  const { themeStretch } = useSettings();
  const [customerList, setCustomerList] = useState([]);
  const { allData, addData } = useSelector((state) => state.customers);
  const { selectedLocation } = useSelector((state) => state.location);
  const isGlobal = selectedLocation?.name === 'Global' || selectedLocation?.id == 1;
  const [smsContent, setSmsContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [sentCount, setSentCount] = useState(0);
  const [totalBatches, setTotalBatches] = useState(0);
  const [gatewayData, setDateWayData] = useState({});
  const [smsCount, setSmsCount] = useState(0);

  // Customer type/date filter
  const [filterType, setFilterType] = useState('none'); // 'none' | 'joined' | 'ordered' | 'birthday'
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [birthdayDate, setBirthdayDate] = useState(null);

  // Location filter (only active when a specific location is selected)
  const [locationFilterType, setLocationFilterType] = useState('visited'); // 'visited' | 'last_order'

  useEffect(() => {
    dispatch(getCustomers());
    getPricing();
  }, []);

  useEffect(() => {
    if (allData?.length > 0) {
      setCustomerList(
        allData
          ?.filter((value) => value?.sendSms)
          ?.map((value) => {
            const content = applySmsPlaceholders(smsContent, value);
            const smsCounter = SmsCounter.count(content);

            return {
              ...value,
              loyalty_data: safeJSONParse(value?.loyalty_data),
              pos_data: safeJSONParse(value?.pos_data),
              content,
              smsCount: smsCounter.messages,
              encordingType: smsCounter.encoding,
            };
          })
      );
    } else {
      setCustomerList([]);
    }
  }, [allData]);

  useEffect(() => {
    dispatch(getCustomers());
  }, [addData]);

  const columns = [
    {
      accessorKey: 'customer_id',
      header: 'ID',
    },
    {
      accessorKey: 'customer_name',
      header: 'Customer Name',
    },
    {
      accessorKey: 'customer_phone',
      header: 'Phone',
    },
    {
      accessorKey: 'customer_birthday',
      header: 'Birthday',
      Cell: ({ row }) => <>{formatBirthday(row?.original?.customer_birthday)}</>,
    },
    {
      accessorKey: 'encordingType',
      header: 'Encoding',
    },
    {
      accessorKey: 'smsCount',
      header: 'SMS Count',
    },
    {
      accessorKey: 'content',
      header: 'SMS Content',
    },
  ];
  useEffect(() => {
    setCustomerList((prevList) => {
      return prevList.map((item) => {
        const content = applySmsPlaceholders(smsContent, item);
        const smsCounter = SmsCounter.count(content);

        return {
          ...item,
          content,
          smsCount: smsCounter.messages,
          encordingType: smsCounter.encoding,
        };
      });
    });
  }, [smsContent]);

  const handleSmsChange = (event) => {
    const newContent = event.target.value;
    setSmsContent(newContent);
  };

  const getPricing = async () => {
    try {
      const body = {
        userId: '22e89e54-6155-46d5-b6ea-8ba86719d3de',
        authToken: '3b044b6c-52d2-42d0-b623-981cf53e2c25',
        senderName: 'BubbleMania',
      };

      const response = await fetch('https://api-bulksms.expergen.com/status', {
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + localStorage.getItem('authToken')?.toString() || '',
        },
        body: JSON.stringify(body),
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error('Network response was not ok ' + response.statusText);
      }

      const data = await response.json();
      setDateWayData(data);
    } catch (error) {
      console.error('Fetch error:', error);
    }
  };

  const sendBatchSms = async (batch) => {
    const body = {
      userId: '22e89e54-6155-46d5-b6ea-8ba86719d3de',
      authToken: '3b044b6c-52d2-42d0-b623-981cf53e2c25',
      senderName: 'BubbleMania',
      data: batch.map((value) => ({ to: value?.customer_phone, content: value?.content })),
    };
    try {
      const response = await fetch('https://api-bulksms.expergen.com/bulkSend', {
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + localStorage.getItem('authToken')?.toString() || '',
        },
        body: JSON.stringify(body),
        method: 'POST',
      });
      if (response.ok) {
        toast.success(`Batch sent successfully.`);
        setSentCount((prev) => prev + 1);
      } else {
        throw new Error('Failed to send batch');
      }
    } catch (error) {
      toast.error(`Error sending batch: ${error.message}`);
    }
  };

  // Derive filtered list based on filter type + date selection + location
  const filteredCustomerList = useMemo(() => {
    // Step 1: customer/date filter
    let result;
    if (filterType === 'none') {
      result = customerList;
    } else if (filterType === 'birthday') {
      const selectedBirthday = getValidMonthDay(birthdayDate);

      if (!selectedBirthday) {
        result = [];
      } else {
        result = customerList.filter((customer) => {
          const customerBirthday = getValidMonthDay(customer?.customer_birthday);
          if (!customerBirthday) return false;

          return customerBirthday.month === selectedBirthday.month && customerBirthday.day === selectedBirthday.day;
        });
      }
    } else if (!startDate || !endDate) {
      result = []; // wait until both dates are chosen
    } else {
      const start = utcMoment(startDate).startOf('day');
      const end = utcMoment(endDate).endOf('day');

      if (filterType === 'joined') {
        result = customerList.filter((customer) => {
          const joinDate = utcMoment(customer?.customer_datetime);
          return joinDate.isSameOrAfter(start) && joinDate.isSameOrBefore(end);
        });
      } else if (filterType === 'ordered') {
        result = customerList.filter((customer) => {
          const orders = customer?.pos_data;
          if (!orders || !Array.isArray(orders)) return false;
          return orders.some((order) => {
            const orderDate = utcMoment(order?.datetime);
            return orderDate.isSameOrAfter(start) && orderDate.isSameOrBefore(end);
          });
        });
      } else {
        result = customerList;
      }
    }

    // Step 2: location filter (skip when Global)
    if (!isGlobal && selectedLocation?.id) {
      const locId = Number(selectedLocation.id);

      if (locationFilterType === 'visited') {
        // Customer has at least one order at this location (within the date range
        // if a date filter is active, otherwise ever)
        result = result.filter((customer) => {
          const orders = customer?.pos_data;
          if (!orders || !Array.isArray(orders)) return false;
          if (filterType !== 'none' && startDate && endDate) {
            const start = utcMoment(startDate).startOf('day');
            const end = utcMoment(endDate).endOf('day');
            return orders.some((order) => {
              const orderDate = utcMoment(order?.datetime);
              return Number(order?.location_id) === locId && orderDate.isSameOrAfter(start) && orderDate.isSameOrBefore(end);
            });
          }
          return orders.some((order) => Number(order?.location_id) === locId);
        });
      } else if (locationFilterType === 'last_order') {
        // Customer's most recent order was placed at this location
        result = result.filter((customer) => {
          const orders = customer?.pos_data;
          if (!orders || !Array.isArray(orders)) return false;
          // pos_data is already ordered DESC by id from the backend
          const lastOrder = orders[0];
          return lastOrder && Number(lastOrder?.location_id) === locId;
        });
      }
    }

    return result;
  }, [customerList, filterType, startDate, endDate, birthdayDate, isGlobal, selectedLocation, locationFilterType]);

  useEffect(() => {
    setSmsCount(filteredCustomerList?.map((value) => value?.smsCount)?.reduce((acc, value) => acc + value, 0) || 0);
  }, [smsContent, filteredCustomerList]);

  const handleSendSms = async () => {
    if (smsContent?.length > 0) {
      if (smsCount * COST_PER_MESSAGE + 1000 < gatewayData?.amount) {
        setLoading(true);
        setSentCount(0);

        const batches = [];
        for (let i = 0; i < filteredCustomerList.length; i += BATCH_SIZE) {
          batches.push(filteredCustomerList.slice(i, i + BATCH_SIZE));
        }
        setTotalBatches(batches.length);

        for (const batch of batches) {
          await sendBatchSms(batch);
        }
        getPricing();
        setLoading(false);
      } else {
        toast.error('No Enough money to send the sms! Also we need to have additional 1000 for Shop SMS!');
      }
    } else {
      toast.error('Enter text before send!');
    }
  };

  return (
    <Page title={`Bulk Sms`}>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading={`Bulk Sms`}
          links={[
            { name: 'Dashboard', href: PATH_DASHBOARD.root },
            {
              name: 'Marketing',
              href: '',
            },
            { name: `Bulk Sms` },
          ]}
          action={<></>}
        />

        {accessVerify('CUSTOMERS_VIEW') ? (
          <>
            <Box sx={{ mt: 4 }}>
              <Card sx={{ p: 3 }}>
                <Typography variant='h5'>Send Bulk SMS (Balance: {fCurrency(gatewayData?.amount)})</Typography>
                <Typography variant='p' style={{ color: 'red' }}>
                  Cost For this SMS: {fCurrency(smsCount * COST_PER_MESSAGE)}
                </Typography>
                <br />
                <Typography variant='p'>SMS Count: {smsCount}</Typography>
                <br />
                <Typography variant='p'>Customer Count: {filteredCustomerList?.length}</Typography>

                {/* ── Date Range + Customer-Type Filter ── */}
                <Box sx={{ mt: 3, mb: 1 }}>
                  <Typography variant='subtitle2' sx={{ mb: 1 }}>
                    Filter Customers By
                  </Typography>
                  <ToggleButtonGroup
                    value={filterType}
                    exclusive
                    onChange={(_, val) => {
                      if (val !== null) {
                        setFilterType(val);
                        setStartDate(null);
                        setEndDate(null);
                        setBirthdayDate(null);
                      }
                    }}
                    size='small'
                  >
                    <ToggleButton value='none'>All Customers</ToggleButton>
                    <ToggleButton value='joined'>Joined in Range</ToggleButton>
                    <ToggleButton value='ordered'>Ordered in Range</ToggleButton>
                    <ToggleButton value='birthday'>Birthday on Day</ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                {/* ── Location Filter (hidden on Global) ── */}
                {!isGlobal && (
                  <Box sx={{ mt: 2, mb: 1 }}>
                    <Typography variant='subtitle2' sx={{ mb: 1 }}>
                      Location Filter &nbsp;
                      <Typography component='span' variant='caption' color='text.secondary'>
                        ({selectedLocation?.name})
                      </Typography>
                    </Typography>
                    <ToggleButtonGroup
                      value={locationFilterType}
                      exclusive
                      onChange={(_, val) => {
                        if (val !== null) setLocationFilterType(val);
                      }}
                      size='small'
                    >
                      <ToggleButton value='visited'>Visited this Location</ToggleButton>
                      <ToggleButton value='last_order'>Last Order from this Location</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                )}

                {filterType !== 'none' && filterType !== 'birthday' && (
                  <Stack direction='row' spacing={2} sx={{ mt: 2, mb: 1 }}>
                    <DatePicker label='Start Date' value={startDate} onChange={(val) => setStartDate(val)} renderInput={(params) => <TextField {...params} size='small' />} />
                    <DatePicker label='End Date' value={endDate} onChange={(val) => setEndDate(val)} minDate={startDate} renderInput={(params) => <TextField {...params} size='small' />} />
                    {startDate && endDate && (
                      <Typography variant='body2' sx={{ alignSelf: 'center', color: 'text.secondary' }}>
                        {filteredCustomerList.length} customer{filteredCustomerList.length !== 1 ? 's' : ''} matched
                      </Typography>
                    )}
                    {filterType !== 'none' && (!startDate || !endDate) && (
                      <Typography variant='body2' sx={{ alignSelf: 'center', color: 'warning.main' }}>
                        Please select both start and end dates
                      </Typography>
                    )}
                  </Stack>
                )}

                {filterType === 'birthday' && (
                  <Stack direction='row' spacing={2} sx={{ mt: 2, mb: 1 }}>
                    <DatePicker label='Birthday Date' value={birthdayDate} onChange={(val) => setBirthdayDate(val)} renderInput={(params) => <TextField {...params} size='small' />} />
                    {birthdayDate && (
                      <Typography variant='body2' sx={{ alignSelf: 'center', color: 'text.secondary' }}>
                        {filteredCustomerList.length} customer{filteredCustomerList.length !== 1 ? 's' : ''} matched
                      </Typography>
                    )}
                    {!birthdayDate && (
                      <Typography variant='body2' sx={{ alignSelf: 'center', color: 'warning.main' }}>
                        Please select a birthday date
                      </Typography>
                    )}
                  </Stack>
                )}

                <Stack spacing={2} mt={2}>
                  <TextField label='SMS Content' multiline rows={4} value={smsContent} onChange={handleSmsChange} fullWidth />
                  <Typography variant='body2'>
                    Message Length: {SmsCounter.count(smsContent)?.length} | Sms Count: {SmsCounter.count(smsContent)?.messages} | Type: {SmsCounter.count(smsContent)?.encoding}
                  </Typography>
                  <Typography variant='body2'>You can replace name with --name-- and loyalty points with --loyaltypoints--</Typography>
                  <Button variant='contained' color='primary' onClick={handleSendSms} disabled={loading}>
                    {loading ? <CircularProgress size={24} /> : 'Send SMS'}
                  </Button>
                  {totalBatches > 0 && (
                    <Typography variant='body2'>
                      Sent {sentCount} of {totalBatches} batches
                    </Typography>
                  )}
                </Stack>
              </Card>
            </Box>
            <DataGridTable
              name={'Pos Sales'}
              data={filteredCustomerList}
              column={columns}
              isLoading={false}
              rowSelection={false}
              setRowSelection={() => {}}
              enableRowActions={false}
              enablePinning={false}
              enableRowSelection={false}
              isRowClickable={true}
              renderRowActionItems={() => []}
            />
          </>
        ) : (
          <PermissionRequired />
        )}
      </Container>
    </Page>
  );
}
